<template>
  <div class="app-guide">
    <h1>System Usage Guide</h1>

    <nav>
      <h2>Index</h2>
      <ol class="nav-depth1">
        <li>
          <a href="#head_1">1. Login Related</a>
          <ol class="nav-depth2">
            <li><a href="#head_1-1">1.1 Sign in</a></li>
            <li><a href="#head_1-2">1.2 Sign up</a></li>
          </ol>
        </li>
        <li>
          <a href="#head_2">2. Dashboard</a>
          <ol class="nav-depth2">
            <li>
              <a href="#head_2-1">2.1 Diagnostics</a>
              <ol class="nav-depth3">
                <li><a href="#head_2-1-0">2.1.0 Overview</a></li>
                <li><a href="#head_2-1-1">2.1.1 Description of each diagnostic type</a></li>
                <li><a href="#head_2-1-2">2.1.2 Advanced options for URL diagnostics</a></li>
                <li><a href="#head_2-1-3">2.1.3 Image diagnostics</a></li>
                <li><a href="#head_2-1-3">2.1.4 Re-diagnosis</a></li>
              </ol>
            </li>
          </ol>
        </li>
        <li>
          <a href="#head_3">3. Diagnostic Results</a>
          <ol class="nav-depth2">
            <li><a href="#head_3-0">3.0 Overview</a></li>
            <li><a href="#head_3-1">3.1 Accessibility Diagnosis Request</a></li>
            <li>
              <a href="#head_3-3">3.2 Diagnostic detailed results</a>
              <ol class="nav-depth3">
                <li><a href="#head_3-2-1">3.2.1 Configuration of Diagnostic Guide</a></li>
                <li><a href="#head_3-2-2">3.2.2 How to improve error items</a></li>
              </ol>
            </li>
          </ol>
        </li>
      </ol>

      <address>
        Contact: <a href="mailto:seeall@seeall.ai">seeall@seeall.ai</a>
      </address>
    </nav>

    <section>
      <h2 id="head_1">1. Login Related</h2>
      <div class="depth2">
        <h3 id="head_1-1">1.1 Sign in</h3>
        <ul>
          <li>First screen of SeeAll Auto Diagnosis System</li>
          <li>System is only available for accounts that have been approved at login</li>
        </ul>
        <div class="img-area">
          <img src="../../../src/assets/images/help/1.1-1.png" alt="">
        </div>
      </div>

      <div class="depth2">
        <h3 id="head_1-2">1.2 Sign up</h3>
        <ul>
          <li>When you complete your membership, you will be in a waiting state for approval, and your administrator can approve or reject this account.</li>
          <li>Email verification is only possible with authorized Samsung-related domains. Therefore, if you need to sign up for an email domain without permission, please contact the administrator.</li>
          <li>System is available after membership is approved</li>
        </ul>
        <div class="img-area">
          <figure class="column">
            <figcaption>&lt; Membership Page &gt;</figcaption>
            <img src="../../../src/assets/images/help/1.2-1.png" alt="">
          </figure>
        </div>
      </div>
    </section>

    <section>
      <h2 id="head_2">2. Dashboard</h2>

      <div class="depth2">
        <h3 id="head_2-1">2.1. Diagnostics</h3>
        <div class="depth3">
          <h4 id="head_2-1-0">2.1.0 Overview</h4>
          <ul>
            <li>You can request 3 types of new diagnosis or re-diagnose an existing diagnosis/saved report.</li>
          </ul>
        </div>

        <div class="depth3">
          <h4 id="head_2-1-1">2.1.1 Description of each diagnostic type</h4>
          <ul>
            <li>Authoring page: You can diagnose the URL created by Authoring type. Authoring type diagnosis is different from the diagnosis scope of URLs made with raw code.</li>
            <li>Rawcode page: Diagnose URLs created with raw code. Since it is not a page created with Authoring in a restricted environment, there are more items that must be adhered to in authoring.</li>
            <li>Image: Diagnose accessibility based on image files. Diagnose brightness contrast and alt text by uploading a Copydeck file with alt text such as title/description of the image along with the image file.</li>
          </ul>
          <div class="img-area"><img src="../../../src/assets/images/help/2.1.1-1.png" alt=""></div>
        </div>

        <div class="depth3">
          <h4 id="head_2-1-2">2.1.2 Advanced options for URL diagnostics</h4>
          <div class="img-area"><img src="../../../src/assets/images/help/2.1.2-1.png" alt=""></div>
          <div class="img-area"><img src="../../../src/assets/images/help/2.1.2-2.png" alt=""></div>
          <ul>
            <li>
              Elements to Exclude:
              <ul class="level2">
                <li>Diagnosis can be performed except for common components on Samsung.com (GNB and Footer are excluded by default).</li>
                <li>
                  In addition to common components, you can also specify areas to exclude by adding a CSS Selector. (CSS Selector of elements to be excluded from developer tools can be extracted)
                  <div class="img-area"><img src="../../../src/assets/images/help/2.1.2-3.png" alt=""></div>
                </li>
              </ul>
            </li>
            <li>
              Login Required Page:
              <ul class="level2">
                <li>If made with P5 or P6, login information is required to access the URL. Activate the option, select a production type (P5/P6), and enter login related information. Selector information and value information for ID, password, and login button are required.</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="depth3">
          <h4 id="head_2-1-3">2.1.3 Image diagnostics</h4>
          <div class="img-area"><img src="../../../src/assets/images/help/2.1.3-1.png" alt=""></div>
          <ul>
            <li>First, upload a Copydeck file with alt text for the image (the template for the file can be downloaded from the diagnosis request screen).</li>
            <li>
              You can check an example of writing in the SAMPLE sheet in the downloaded Excel template. Please enter your information on the IMAGE sheet.
              <div class="img-area"><img src="../../../src/assets/images/help/2.1.3-2.png" alt=""></div>
            </li>
            <li>
              When you upload an Excel file, alternative text information is automatically entered. Please upload an image of the appropriate area in each alt text section.
              <div class="img-area"><img src="../../../src/assets/images/help/2.1.3-3.png" alt=""></div>
            </li>
            <li>Once the image upload is complete, you can start diagnosing.</li>
          </ul>
        </div>

        <div class="depth3">
          <h4 id="head_2-1-4">2.1.4 Re-diagnosis</h4>
          <div class="img-area"><img src="../../../src/assets/images/help/2.1.4-1.png" alt=""></div>
          <ul>
            <li>Reports saved after diagnosis in the past can be re-diagnosed.</li>
            <li>URL type diagnosis can be re-diagnosed immediately, and image type diagnosis requires uploading a new Excel file and image.</li>
            <li>
              When you save the rediagnosed report again, it is sequentially stacked on the existing report in the dashboard and saved.
              <div class="img-area"><img src="../../../src/assets/images/help/2.1.4-2.png" alt=""></div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section>
      <h2 id="head_3">3. Diagnostic Results</h2>
      <div class="depth2">
        <h3 id="head_3-0">3.0 Overview</h3>
        <div class="img-area"><img src="../../../src/assets/images/help/3.0-1.png" alt=""></div>
        <ul>
          <li>You can check the results for each type of diagnosis. Diagnosis results can be checked in the top tab.</li>
        </ul>
      </div>

      <div class="depth2">
        <h3 id="head_3-1">3.1 Accessibility Diagnosis Request</h3>
        <div class="img-area"><img src="../../../src/assets/images/help/3.1-1.png" alt=""></div>
        <div class="img-area"><img src="../../../src/assets/images/help/3.1-2.png" alt=""></div>
        <ul>
          <li>Passed reports can request diagnosis from the accessibility inspection team. Create a Jira Ticket at op-jra.samsung.com with the share information in the report. (share feature guide: 3.3.1)</li>
          <li>Fail report should review detailed diagnostic results and improve work content (URL, image, alt text, etc.) so that it can be passed.</li>
        </ul>
      </div>

      <div class="depth2">
        <h3 id="head_3-2">3.2 Diagnostic detailed results</h3>

        <div class="depth3">
          <h4 id="head_3-2-1">3.2.1 Configuration of Diagnostic Guide</h4>
          <ul>
            <li>The diagnostic guide consists of a total of 3depths. Items and descriptions for each depth are as follows.</li>
            <li>
              Checklist: A top-level guide to complying with accessibility according to SWCAG. The checklist consists of 21 guides and more.
              <div class="img-area"><img src="../../../src/assets/images/help/3.2.1-1.png" alt=""></div>
              <div class="img-area"><img src="../../../src/assets/images/help/3.2.1-2.png" alt=""></div>
            </li>
            <li>
              Category (of checklist): This is a category that categorizes similar types of rules by checklist. You can also refer to the WCAG number to understand the category.
              <div class="img-area"><img src="../../../src/assets/images/help/3.2.1-3.png" alt=""></div>
            </li>
            <li>
              Detailed rules: You can check detailed error rules by category. Please refer to the explanation of the detailed error rule and the guide on how to improve it.
              <div class="img-area"><img src="../../../src/assets/images/help/3.2.1-4.png" alt=""></div>
            </li>
          </ul>
        </div>

        <div class="depth3">
          <h4 id="head_3-2-2">3.2.2 How to improve error items</h4>
          <ul>
            <li>
              If you click the error item in the report area, you can check the problematic part in the capture screen and code area.
              <div class="img-area"><img src="../../../src/assets/images/help/3.2.2-1.png" alt=""></div>
            </li>
            <li>
              Areas hidden by CSS and Script cannot be checked directly on the capture screen, so please check the code.
              <div class="img-area"><img src="../../../src/assets/images/help/3.2.2-2.png" alt=""></div>
            </li>
          </ul>
        </div>
      </div>

      <div class="depth2">
        <h3 id="head_3-3">3.3 Report management function</h3>

        <div class="depth3">
          <h4 id="head_3-3-1">3.3.1 Report Sharing</h4>
          <div class="img-area"><img src="../../../src/assets/images/help/3.3.1-1.png" alt=""></div>
          <ul>
            <li>
              Saved reports can be shared in the form of a URL. However, for security, you must create a password and set it so that only people with the password can access it.
              <div class="img-area"><img src="../../../src/assets/images/help/3.3.1-2.png" alt=""></div>
              <div class="img-area"><img src="../../../src/assets/images/help/3.3.1-3.png" alt=""></div>
            </li>
          </ul>
        </div>

        <div class="depth3">
          <h4 id="head_3-3-2">3.3.2 Saving the report</h4>
          <div class="img-area"><img src="../../../src/assets/images/help/3.3.2-1.png" alt=""></div>
          <ul>
            <li>You can save the report. Saved reports can be used for re-diagnosing or sharing.</li>
          </ul>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'AppGuide'
}
</script>

<style lang="scss" scoped>
.app-guide {
  width: 1194px;
  padding: 0 15px;
  margin: 15px auto 0;

  nav {
    border-radius: 10px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #ffffff;
    padding: 20px 20px 50px;
    margin-bottom: 60px;
    height: 600px;

    address {
      font-style: normal;
      color: #999999;
      margin: 580px 5px 0;
      font-size: 13px;
      text-align: right;

      a {
        text-decoration: underline;
      }
    }

    h2 {
      border-radius: 4px;
      box-shadow: inset 0 1px 3px 0 rgba(0, 0, 0, 0.12);
      background-color: #f5f5f5;
      font-size: 13px;
      font-weight: bold;
      color: #666666;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding: 5px 10px;
      margin-bottom: 30px;
    }

    ol{
      position: relative;
    }

    .nav-depth1 > li {
      height: 16px;
      font-size: 16px;
      font-weight: bold;
      line-height: 1;
      letter-spacing: normal;
      position: absolute;

      &:first-child {
        top: 0;
        left: 1%;
      }

      &:nth-child(2) {
        top: 0;
        left: 34%;
      }

      &:last-child {
        top: 0;
        left: 69%;
      }
    }

    .nav-depth2,
    .nav-depth3 {
      margin-top: 16px;
      padding-left: 20px;
    }

    .nav-depth2 > li {
      font-size: 16px;
      font-weight: normal;
      font-style: normal;
      line-height: 24px;
      letter-spacing: normal;
      margin-bottom: 16px;
    }

    .nav-depth3 > li {
      font-size: 16px;
      font-weight: normal;
      line-height: 24px;
      letter-spacing: normal;
      margin-bottom: 16px;
    }

    .nav-depth1 > li a,
    .nav-depth2 > li a {
      color: #1e1e1e;
    }

    .nav-depth3 > li a {
      color: #666;
    }
  }

  h1 {
    font-size: 30px;
    font-weight: 600;
    color: #151515;
    padding: 40px 0 13px;
    display: inline-block;
    margin: 0 10px;
  }

  section {
    border-radius: 10px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #ffffff;
    padding: 20px 20px 50px;
    margin-bottom: 60px;

    &:last-child {
      margin-bottom: 0;
    }

    h2 {
      font-size: 20px;
      font-weight: 600;
      color: #151515;
      padding: 0 0 15px;
      margin: 0 5px;
    }

    h3 {
      border-radius: 4px;
      box-shadow: inset 0 1px 3px 0 rgba(0, 0, 0, 0.12);
      background-color: #f5f5f5;
      font-size: 13px;
      font-weight: bold;
      color: #666666;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding: 5px 10px;
      margin-bottom: 30px;
    }

    h4 {
      font-size: 14px;
      font-weight: 600;
      padding: 0 0 15px;
      margin: 0 10px;
      color: #666666;
    }

    .img-area {
      font-size: 13px;
      text-align: center;
      margin: 50px 0;

      img {
        max-width: 100%;
      }

      figure {
        display: inline-block;
        text-align: left;
        margin: 0 10px;
      }

      figcaption {
        margin-bottom: 5px;
        color: #999999;
      }
    }

    p {
      font-size: 14px;
      color: #1e1e1e;
      line-height: 1.5;
      margin: 0 10px 30px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ol,
    ul {
      font-size: 14px;
      font-weight: normal;
      color: #1e1e1e;
      line-height: 1.5;
      margin: 0 10px 30px;

      li {
        list-style: none;
        padding-left: 15px;
        margin-left: 15px;
        position: relative;
      }
    }

    ul {
      li {
        margin-left: 20px;

        &:before {
          content: '•';
          position: absolute;
          left: 0;
        }
      }
    }

    a {
      color: #386ae8;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/valid-member/AppGuide.vue